import { Trans, useTranslation } from "react-i18next";

import { Dropdown, Form, useForm } from "capitalrx-components-2.0/lib/forms";
import { HorizontalLine } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { useUpdateMemberPaymentConfigurationMutation } from "@/App/api/medicarePrescriptionPaymentPlan";
import { ThemedButton, ThemedSubmitButton } from "@/App/components";
import { MemberPaymentConfiguration } from "@/App/types/medicarePrescriptionPaymentPlan";
import { getPaymentDayOptionsArray } from "@/App/utils/dates";
import { usePlanYear } from "../../PlanYearContext";

interface ModifyPaymentDateFormProps {
  onFormClose: () => void;
  paymentConfiguration: MemberPaymentConfiguration;
}

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.modifyPaymentDateForm";

export const ModifyPaymentDateForm = ({ onFormClose, paymentConfiguration }: ModifyPaymentDateFormProps) => {
  const { t: translate, i18n } = useTranslation();
  const planYear = usePlanYear();

  const form = useForm({
    defaultValues: {
      paymentDate: paymentConfiguration?.preferred_payment_day,
    },
  });

  const { mutateAsync: updatePaymentConfiguration, isPending: isLoading } = useUpdateMemberPaymentConfigurationMutation();

  const paymentDayOptionsArray = getPaymentDayOptionsArray(
    (month_day: string) => <Trans i18nKey={`${TRANSLATION_PATH}.monthDayOption`} values={{ month_day }} />,
    i18n.language,
  );

  const handleAutoPaymentDateFormSubmit = async () => {
    await updatePaymentConfiguration({
      preferred_payment_day: +form.getValues().paymentDate,
      preferred_payment_method: paymentConfiguration?.preferred_payment_method,
      plan_year: planYear,
    });
    onFormClose();
  };

  return (
    <>
      <HorizontalLine />
      <Form onSubmit={handleAutoPaymentDateFormSubmit} form={form}>
        <Stack>
          <Body size="medium">
            <Semibold>{translate(`${TRANSLATION_PATH}.changeAutomaticPaymentDate`)}</Semibold>
          </Body>
          <Dropdown.Field
            name="paymentDate"
            label={translate(`${TRANSLATION_PATH}.automaticPaymentDate`)}
            instructions={translate(`${TRANSLATION_PATH}.automaticPaymentDateDescription`)}
            required
          >
            {paymentDayOptionsArray.map(({ key, label }) => {
              return (
                <Dropdown.Option key={key} value={key}>
                  {label}
                </Dropdown.Option>
              );
            })}
          </Dropdown.Field>
          <Shelf spacing="small">
            <ThemedSubmitButton testId="save" appearance="primary" disabled={isLoading}>
              {translate(`${TRANSLATION_PATH}.save`)}
            </ThemedSubmitButton>
            <ThemedButton testId="cancel" appearance="secondary" onClick={onFormClose}>
              {translate(`${TRANSLATION_PATH}.cancel`)}
            </ThemedButton>
            <Shelf.Spacer />
          </Shelf>
        </Stack>
      </Form>
      <HorizontalLine />
    </>
  );
};
